/**
 * Šprtec 3D — sdílená 2D fyzika hrací plochy.
 * Běží identicky na serveru (autoritativní online hra) i v prohlížeči
 * (singleplayer, lokální duel, míření).
 *
 * Souřadnice: x = šířka stolu, z = délka. Hráč strany 0 brání branku
 * u z = -L/2 a útočí na z = +L/2; strana 1 obráceně.
 */

export const RINK = {
  L: 3.0,   // délka hrací plochy
  W: 1.9,   // šířka
  CR: 0.3,  // poloměr zaoblení rohů
  GW: 0.46, // šířka ústí branky
  GD: 0.2,  // hloubka branky
};

export const PIECE_R = 0.085;
export const PUCK_R = 0.05;
export const PIECE_M = 3.0;
export const PUCK_M = 0.8;

export const MAX_SHOT_SPEED = 7.0;
export const MIN_SHOT_SPEED = 0.4;
export const BODY_REST = 0.92; // odrazivost kámen–kámen
export const WALL_REST = 0.7;  // odrazivost od mantinelu
export const DAMP = 0.42;      // exponenciální útlum rychlosti [1/s]
export const MU = 0.28;        // konstantní zpomalení třením [m/s²]
export const STOP_EPS = 0.045; // pod touto rychlostí považujeme těleso za stojící
export const SIM_MAX_TIME = 9; // pojistka: maximální délka simulace jednoho tahu
export const PHYS_DT = 1 / 120;

const L2 = RINK.L / 2;
const W2 = RINK.W / 2;
const GW2 = RINK.GW / 2;
const { CR, GD } = RINK;

/**
 * Mantinely jako úsečky [x1, z1, x2, z2, nx, nz]; (nx, nz) je normála
 * směřující VEN z hrací plochy (využívá ji vykreslování mantinelů).
 * Ústí branek je v koncových stěnách vynechané a napojené na brankoviště.
 */
export const SEGMENTS = [
  // boční mantinely
  [-W2, -(L2 - CR), -W2, L2 - CR, -1, 0],
  [W2, -(L2 - CR), W2, L2 - CR, 1, 0],
  // koncové stěny u z = +L2 (s mezerou pro branku)
  [-(W2 - CR), L2, -GW2, L2, 0, 1],
  [GW2, L2, W2 - CR, L2, 0, 1],
  // koncové stěny u z = -L2
  [-(W2 - CR), -L2, -GW2, -L2, 0, -1],
  [GW2, -L2, W2 - CR, -L2, 0, -1],
  // brankoviště +z
  [-GW2, L2, -GW2, L2 + GD, -1, 0],
  [GW2, L2, GW2, L2 + GD, 1, 0],
  [-GW2, L2 + GD, GW2, L2 + GD, 0, 1],
  // brankoviště -z
  [-GW2, -L2, -GW2, -(L2 + GD), -1, 0],
  [GW2, -L2, GW2, -(L2 + GD), 1, 0],
  [-GW2, -(L2 + GD), GW2, -(L2 + GD), 0, -1],
];

/** Středy čtvrtkruhových rohů. */
export const CORNERS = [
  [-(W2 - CR), -(L2 - CR)],
  [W2 - CR, -(L2 - CR)],
  [-(W2 - CR), L2 - CR],
  [W2 - CR, L2 - CR],
];

/** Základní rozestavení jedné strany (pro stranu 0, z záporné). */
export const FORMATION = [
  { x: 0, z: -(L2 - 0.16) },    // brankář
  { x: -0.42, z: -(L2 - 0.62) }, // obránci
  { x: 0.42, z: -(L2 - 0.62) },
  { x: -0.55, z: -0.32 },        // útočníci
  { x: 0.55, z: -0.32 },
];

export const PUCK_ID = 10;

/** Vytvoří všechna tělesa v základním rozestavení. */
export function makeBodies() {
  const bodies = [];
  let id = 0;
  for (let side = 0; side <= 1; side++) {
    const mz = side === 0 ? 1 : -1;
    for (const f of FORMATION) {
      bodies.push({
        id: id++,
        kind: 'piece',
        side,
        r: PIECE_R,
        m: PIECE_M,
        x: f.x,
        z: f.z * mz,
        vx: 0,
        vz: 0,
        home: { x: f.x, z: f.z * mz },
      });
    }
  }
  bodies.push({
    id: PUCK_ID,
    kind: 'puck',
    side: null,
    r: PUCK_R,
    m: PUCK_M,
    x: 0,
    z: 0,
    vx: 0,
    vz: 0,
    home: { x: 0, z: 0 },
  });
  return bodies;
}

/** Vrátí všechna tělesa na výchozí pozice a zastaví je. */
export function resetPositions(bodies) {
  for (const b of bodies) {
    b.x = b.home.x;
    b.z = b.home.z;
    b.vx = 0;
    b.vz = 0;
  }
}

export function speedOf(b) {
  return Math.hypot(b.vx, b.vz);
}

export function allStopped(bodies) {
  return bodies.every((b) => speedOf(b) < STOP_EPS);
}

export function stopAll(bodies) {
  for (const b of bodies) {
    b.vx = 0;
    b.vz = 0;
  }
}

function collideBodies(a, b, events) {
  const dx = b.x - a.x;
  const dz = b.z - a.z;
  const dist = Math.hypot(dx, dz);
  const minDist = a.r + b.r;
  if (dist >= minDist || dist === 0) return;

  const nx = dx / dist;
  const nz = dz / dist;
  const invA = 1 / a.m;
  const invB = 1 / b.m;
  const invSum = invA + invB;

  // vytlačení z průniku (podle hmotností)
  const overlap = minDist - dist;
  a.x -= nx * overlap * (invA / invSum);
  a.z -= nz * overlap * (invA / invSum);
  b.x += nx * overlap * (invB / invSum);
  b.z += nz * overlap * (invB / invSum);

  const rvx = b.vx - a.vx;
  const rvz = b.vz - a.vz;
  const vn = rvx * nx + rvz * nz;
  if (vn >= 0) return;

  const j = (-(1 + BODY_REST) * vn) / invSum;
  a.vx -= (j * nx) * invA;
  a.vz -= (j * nz) * invA;
  b.vx += (j * nx) * invB;
  b.vz += (j * nz) * invB;

  if (events && -vn > 0.15) {
    events.push({
      t: 'hit',
      s: Math.min(1, -vn / 5),
      x: (a.x + b.x) / 2,
      z: (a.z + b.z) / 2,
      puck: a.kind === 'puck' || b.kind === 'puck',
    });
  }
}

function collideSegment(b, x1, z1, x2, z2, events) {
  const abx = x2 - x1;
  const abz = z2 - z1;
  const lenSq = abx * abx + abz * abz;
  let t = ((b.x - x1) * abx + (b.z - z1) * abz) / lenSq;
  t = t < 0 ? 0 : t > 1 ? 1 : t;
  const cx = x1 + abx * t;
  const cz = z1 + abz * t;
  let dx = b.x - cx;
  let dz = b.z - cz;
  const d = Math.hypot(dx, dz);
  if (d >= b.r || d === 0) return;

  dx /= d;
  dz /= d;
  b.x = cx + dx * b.r;
  b.z = cz + dz * b.r;
  const vn = b.vx * dx + b.vz * dz;
  if (vn < 0) {
    b.vx -= (1 + WALL_REST) * vn * dx;
    b.vz -= (1 + WALL_REST) * vn * dz;
    if (events && -vn > 0.25) {
      events.push({ t: 'wall', s: Math.min(1, -vn / 5), x: b.x, z: b.z });
    }
  }
}

function collideCorners(b, events) {
  for (const [cx, cz] of CORNERS) {
    // těleso je v „rohové výseči“ jen pokud leží za začátkem oblouku
    if (Math.sign(b.x) !== Math.sign(cx) || Math.sign(b.z) !== Math.sign(cz)) continue;
    if (Math.abs(b.x) < Math.abs(cx) || Math.abs(b.z) < Math.abs(cz)) continue;

    const dx = b.x - cx;
    const dz = b.z - cz;
    const d = Math.hypot(dx, dz);
    const maxD = CR - b.r;
    if (d <= maxD || d === 0) continue;

    const nx = dx / d; // ven z oblouku
    const nz = dz / d;
    b.x = cx + nx * maxD;
    b.z = cz + nz * maxD;
    const vn = b.vx * nx + b.vz * nz;
    if (vn > 0) {
      b.vx -= (1 + WALL_REST) * vn * nx;
      b.vz -= (1 + WALL_REST) * vn * nz;
      if (events && vn > 0.25) {
        events.push({ t: 'wall', s: Math.min(1, vn / 5), x: b.x, z: b.z });
      }
    }
  }
}

/**
 * Jeden fyzikální krok. Volat s pevným dt = PHYS_DT.
 * Do `events` přidává události nárazů (pro zvuky/efekty).
 */
export function stepPhysics(bodies, dt, events) {
  for (const b of bodies) {
    b.x += b.vx * dt;
    b.z += b.vz * dt;

    const sp = Math.hypot(b.vx, b.vz);
    if (sp > 0) {
      let ns = sp * Math.exp(-DAMP * dt) - MU * dt;
      if (ns < 0.002) ns = 0;
      const k = sp === 0 ? 0 : ns / sp;
      b.vx *= k;
      b.vz *= k;
    }
  }

  for (let i = 0; i < bodies.length; i++) {
    for (let j = i + 1; j < bodies.length; j++) {
      collideBodies(bodies[i], bodies[j], events);
    }
  }

  for (const b of bodies) {
    for (const s of SEGMENTS) {
      collideSegment(b, s[0], s[1], s[2], s[3], events);
    }
    collideCorners(b, events);
  }
}

/**
 * „Vystřelí“ kotouč o poloměru r z bodu (x, z) směrem (dx, dz) a vrátí
 * vzdálenost + druh první překážky. Slouží AI a mířicí lince.
 * Jednoduchý pochodový algoritmus — přesnost stačí, výkon nevadí.
 */
export function castDisc(x, z, dx, dz, r, bodies, ignoreIds = [], maxDist = 4) {
  const len = Math.hypot(dx, dz);
  if (len === 0) return { dist: 0, hit: null };
  dx /= len;
  dz /= len;
  const step = 0.015;
  const probe = { x, z, r, vx: 0, vz: 0 };

  for (let d = step; d <= maxDist; d += step) {
    probe.x = x + dx * d;
    probe.z = z + dz * d;

    for (const b of bodies) {
      if (ignoreIds.includes(b.id)) continue;
      const dd = Math.hypot(probe.x - b.x, probe.z - b.z);
      if (dd < r + b.r) return { dist: d, hit: b };
    }

    for (const s of SEGMENTS) {
      const abx = s[2] - s[0];
      const abz = s[3] - s[1];
      const lenSq = abx * abx + abz * abz;
      let t = ((probe.x - s[0]) * abx + (probe.z - s[1]) * abz) / lenSq;
      t = t < 0 ? 0 : t > 1 ? 1 : t;
      const ddx = probe.x - (s[0] + abx * t);
      const ddz = probe.z - (s[1] + abz * t);
      if (Math.hypot(ddx, ddz) < r) return { dist: d, hit: { kind: 'wall' } };
    }

    for (const [cx, cz] of CORNERS) {
      if (Math.sign(probe.x) !== Math.sign(cx) || Math.sign(probe.z) !== Math.sign(cz)) continue;
      if (Math.abs(probe.x) < Math.abs(cx) || Math.abs(probe.z) < Math.abs(cz)) continue;
      if (Math.hypot(probe.x - cx, probe.z - cz) > CR - r) return { dist: d, hit: { kind: 'wall' } };
    }
  }
  return { dist: maxDist, hit: null };
}
